import { Button, Divider, Flex } from '@aws-amplify/ui-react';
import React, { useState } from 'react';
import { TbExternalLink } from 'react-icons/tb';
import ReactModal from 'react-modal';
import General from '../utils/GeneralUtils';

export interface CreateCompaniesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<{ id: string }>;
  crmName: string;
}

type ModalState = 'initial' | 'loading' | 'success' | 'error';

const CreateCompaniesModal: React.FC<CreateCompaniesModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  crmName
}) => {
  const [state, setState] = useState<ModalState>('initial');
  const [newExportId, setNewExportId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setState('loading');
    setError(null);
    try {
      const result = await onConfirm();
      setNewExportId(result.id);
      setState('success');
    } catch (err: any) {
      setError(err?.message || 'Failed to create export');
      setState('error');
    }
  };

  const handleClose = () => {
    setState('initial');
    setNewExportId(null);
    setError(null);
    onClose();
  };

  const isLoading = state === 'loading';
  const isSuccess = state === 'success';
  const isError = state === 'error';

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={handleClose}
      style={General.getModalDefaults({ width: '600px' })}
      closeTimeoutMS={200}
      parentSelector={General.getModalRoot}
    >
      <Flex direction='column' gap={20}>
        <h3 style={{ marginTop: 0, marginBottom: 0 }}>
          Create Companies
        </h3>

        <div>
          <p style={{ marginTop: 0 }}>
            This will create a new <strong>companies export</strong> with the companies that don't exist in your {crmName} account.
          </p>
          <p>
            The new export will be created <strong>in a new tab</strong> for you to review.
          </p>
          <p style={{ marginBottom: 0 }}>
            Once complete, <strong>return to this tab, refresh, and complete</strong> the export.
          </p>
        </div>

        <Flex gap={10} justifyContent='flex-end'>
          <Button
            size='small'
            variation='primary'
            onClick={handleConfirm}
            isLoading={isLoading}
            loadingText='Creating...'
            isDisabled={isLoading || isSuccess}
          >
            Create Companies Export
          </Button>
        </Flex>

        {isSuccess && newExportId && (
          <>
            <Divider size='small' />
            <Flex alignItems='center' justifyContent='space-between'>
              <span>Companies export created successfully.</span>
              <a href={`/exports/${newExportId}`} target="_blank" rel="noopener noreferrer">
                <Button size='small' variation='primary' gap="4px">
                  Go to Export <TbExternalLink size={14} />
                </Button>
              </a>
            </Flex>
          </>
        )}

        {isError && (
          <>
            <Divider size='small' />
            <Flex alignItems='center' justifyContent='space-between'>
              <span style={{ color: 'var(--amplify-colors-red-60)' }}>{error}</span>
              <Button size='small' variation='primary' onClick={handleConfirm}>
                Retry
              </Button>
            </Flex>
          </>
        )}
      </Flex>
    </ReactModal>
  );
};

export default CreateCompaniesModal;
